import React, { useEffect, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { isMobile } from 'react-device-detect';
import Dropdown, { DropdownTheme } from 'Douleutaras/components/Dropdown/Dropdown';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import {
  getNotifications, getNotificationsStats, getUnreadDailyWeeklyNotifications, markNotificationAsRead,
  notificationEndpoint
} from 'Douleutaras/redux/data/notifications/notificationsSlice';
import classNames from 'classnames';
import { useGroupByDate } from 'Douleutaras/hooks/notifications/groupByDate';
import { humanReadableFormat } from 'Douleutaras/utilities/formatting/date.utils';
import { checkSummaryCondition } from 'Douleutaras/redux/data/notifications/notifications.helper';
import { getAbsoluteUrl } from 'Douleutaras/utilities/url-builder';
import { isMobileWebviewUserAgent } from 'Douleutaras/app/lib/mobileUtilities';

import { NotificationEmptyState, NotificationItem, NotificationLoader } from './index';
import './NotificationsDropdown.scss';

export const NotificationsDropdown = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const notificationsState = useAppSelector(state => state.notifications);
  // @ts-ignore
  const notificationsMeta = useAppSelector(state => state.notifications.meta?.[notificationEndpoint]?.meta);
  const notifications = useGroupByDate();

  const isProDashboard = useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.BIDS'),
    end: false,
    caseSensitive: true
  });
  const isMobileWebview = isMobileWebviewUserAgent();

  const [dropdown, setDropdown] = useState(false);

  const openCallback = status => {
    if (notificationsMeta && !notifications.length) {
      dispatch(getNotifications({}));
    }
    setDropdown(status);
  };

  const onScroll = ref => {
    if (ref.currentTarget && notificationsMeta) {
      const { scrollTop, scrollHeight, clientHeight } = ref.currentTarget as HTMLDivElement;
      const scrollPosition = scrollHeight - scrollTop - clientHeight;
      const page = notificationsMeta.pagination.page + 1;
      const pages = notificationsMeta.pagination.pages;
      if (scrollPosition === 0 && pages >= page && !notificationsState.loading) {
        dispatch(getNotifications({ page }));
      }
    }
  };

  const IsNotificationsRoute = useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.NOTIFICATIONS_CENTER'),
    end: false,
    caseSensitive: true
  });

  const checkNotificationsRedirect = async (): Promise<void | 'default'> => {
    // We need to check unread daily, weekly notifications first
    const res = await dispatch(getUnreadDailyWeeklyNotifications());

    if (!res.payload) {
      // tslint:disable-next-line:no-null-keyword
      return null;
    }

    if (res.meta.requestStatus !== 'fulfilled' || !res.payload.data.length) {
      return 'default';
    }
    // If we have some, then calculate it and redirect the user
    const summaryCondition = checkSummaryCondition(res.payload.data);

    // If a device is webview, or a page is not a pro's dashboard, or time is not between given range
    if (isMobileWebview || !isProDashboard || !summaryCondition.isTimeRight) {
      return 'default';
    }

    // If notification id exists, then mark it as read
    if (summaryCondition.id) {
      await dispatch(markNotificationAsRead({ notificationId: summaryCondition.id }));
    }

    navigate(summaryCondition.redirect);
  };

  useEffect(() => {
    // When a pro opens the dashboard in the morning (between new Daily summary generation) and 12:00,
    // we should automatically open the Daily summary
    checkNotificationsRedirect()
      .then(res => {
        // If no -> go with the default flow
        if (res === 'default') {
          dispatch(getNotificationsStats({
            'filter[is_read]': 'false'
          }));
        }
      });
  }, []);

  const header = (
    <header
      styleName="header-notif"
      className={classNames({
        'gtm-notifications_center-all_notifications': isMobile,
        'gtm-notifications_center-bell': !isMobile
      })}
      data-tracking={isMobile ? 'pro-dashboard-navbar__notification' : 'notifications_center__all-notifications'}
    >
      <button styleName={classNames({
            'header-notif__link': true,
            'header-notif__link--active': Boolean(IsNotificationsRoute)
          })} onClick={e => isMobile ? navigate(getAbsoluteUrl('PRO_DASHBOARD.NOTIFICATIONS_CENTER')) : e.preventDefault()}>
        <span styleName="header-notif__icon" className="dashicon dashicon-bell"/>
        { notificationsMeta && notificationsMeta.unreadCount > 0 &&
          <span styleName="header-notif__count">
              {notificationsMeta.unreadCount > 99 ? '99+' : notificationsMeta.unreadCount}
            </span>
        }
        <span styleName="header-notif__title">{t('T.NC Notifications')}</span>
      </button>
    </header>
  );

  return (
    <div styleName="header-notif-dropdown">
      <article className="user-dropdown">
        { isMobile ? header :
          <Dropdown
            callback={openCallback}
            isOpen={dropdown}
            theme={DropdownTheme.PLAIN}
            header={header}
            showHeader
            footer={
              <footer styleName="user-dropdown-footer">
                  <Link to={getAbsoluteUrl('PRO_DASHBOARD.NOTIFICATIONS_CENTER')}
                        styleName="user-dropdown-footer__link"
                        className="gtm-notifications_center-all_notifications"
                  >
                    <span styleName="user-dropdown-footer__title">{t('T.NC See all notifications')}</span>
                  </Link>
              </footer>}
          >
            <Scrollbars style={{ height: isMobile ? '100%' : 470 }} onScroll={onScroll}>
              <div>
                { notifications.length > 0 &&
                notifications.map((item, i) => (
                  <React.Fragment key={`notificationItemWrapper-${i}`}>
                    <div styleName="header-notif-dropdown__date">{humanReadableFormat(item.date, t)} ({item.notifications.length})</div>
                    { item.notifications.map((notification, j) => (
                      <NotificationItem key={`notificationItem${i * 10 + j}`}
                                        id={notification.id}
                                        notification={notification.attributes}
                                        onClick={() => openCallback(false)}
                      />
                    )) }
                  </React.Fragment>
                ))
                }
                { notificationsState.loading && <NotificationLoader width="100%" /> }
                { !notificationsState.loading && !notifications.length && <NotificationEmptyState /> }
              </div>
            </Scrollbars>
          </Dropdown>
        }
      </article>
    </div>
  );
};
